/**
 * Regras da tela da Cobrança na Visão Geral (#6502): João Henrique e Bruna.
 *
 * Pergunta da tela: quem eu preciso contatar hoje, e quanto já recuperei?
 *   - régua da minha carteira: as ações D+4, D+10, D+17 e D+24 (`cobranca_acao_manual`)
 *     com a pessoa como responsável, do dia e atrasadas (mesma conta da Gestão do Financeiro);
 *   - promessas: clientes da carteira marcados como "negociado", pela data combinada;
 *   - recuperado e comissão: a regra do plano de bônus (seção 5): boleto pago mais de 3
 *     dias depois do vencimento, atribuído a quem registrou o último contato entre o
 *     vencimento e o pagamento, com o percentual da faixa de atraso.
 *
 *   node --experimental-strip-types --test src/lib/visaoGeral/__tests__/cobranca.test.ts
 */
import { diaDaAcao, type AcaoDaRegua } from './financeiro.ts';

export const ETAPAS_REGUA = ['D+4', 'D+10', 'D+17', 'D+24'] as const;

const soDigitos = (v: string | null | undefined): string => (v ?? '').replace(/\D/g, '').replace(/^0+/, '');
const somarDias = (iso: string, dias: number) => {
  const [a, m, d] = iso.slice(0, 10).split('-').map(Number);
  const dt = new Date(a, m - 1, d + dias);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
/** Data local ('AAAA-MM-DD') de um instante ISO. */
const diaLocal = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const diasEntre = (deIso: string, ateIso: string): number => {
  const [a1, m1, d1] = deIso.slice(0, 10).split('-').map(Number);
  const [a2, m2, d2] = ateIso.slice(0, 10).split('-').map(Number);
  return Math.round((new Date(a2, m2 - 1, d2).getTime() - new Date(a1, m1 - 1, d1).getTime()) / 86_400_000);
};

// ─────────────────────────── Régua da carteira ───────────────────────────

export interface AcaoDaCarteira extends AcaoDaRegua {
  id: string;
  cliente_nome: string | null;
  valor_total: number | string | null;
}

export interface AcaoComDia extends AcaoDaCarteira {
  /** Dia em que a etapa deve ser feita (vencimento + N). */
  dia: string;
}

export interface ReguaDaCarteira {
  hoje: AcaoComDia[];
  /** Mais recentes primeiro: a dívida nova ainda responde ao contato. */
  atrasadas: AcaoComDia[];
  porEtapa: { etapa: string; hoje: number; atrasadas: number }[];
  valorAtrasado: number;
  maisAntiga: string | null;
}

/** Ações pendentes da régua com a pessoa como responsável, do dia e atrasadas. */
export function reguaDaCarteira(acoes: readonly AcaoDaCarteira[], pessoaId: string, hojeIso: string): ReguaDaCarteira {
  const hoje: AcaoComDia[] = [];
  const atrasadas: AcaoComDia[] = [];
  for (const a of acoes) {
    if (a.status !== 'pendente' || a.responsavel_id !== pessoaId) continue;
    const dia = diaDaAcao(a);
    if (dia > hojeIso) continue;
    (dia === hojeIso ? hoje : atrasadas).push({ ...a, dia });
  }
  atrasadas.sort((x, y) => y.dia.localeCompare(x.dia));
  const etapas = [...new Set([...ETAPAS_REGUA, ...[...hoje, ...atrasadas].map(a => a.etapa)])];
  return {
    hoje,
    atrasadas,
    porEtapa: etapas.map(etapa => ({
      etapa,
      hoje: hoje.filter(a => a.etapa === etapa).length,
      atrasadas: atrasadas.filter(a => a.etapa === etapa).length,
    })),
    valorAtrasado: atrasadas.reduce((t, a) => t + (Number(a.valor_total) || 0), 0),
    maisAntiga: atrasadas.length ? atrasadas[atrasadas.length - 1].dia : null,
  };
}

// ─────────────────────────── Promessas ───────────────────────────

export interface ClienteDaCarteira {
  id: string;
  cliente_nome: string | null;
  cpf_cnpj_cliente: string | null;
  responsavel_cobranca_id: string | null;
  status_negociacao: string | null;
  valor_negociado: number | string | null;
  data_pagamento_negociado: string | null;
}

export interface PromessasDaCarteira {
  hoje: ClienteDaCarteira[];
  /** Data combinada já passou e o cliente segue como "negociado": conferir se pagou. */
  vencidas: ClienteDaCarteira[];
  /** Combinadas para os próximos 7 dias. */
  proximas: ClienteDaCarteira[];
}

/**
 * Promessa = cliente da carteira da pessoa marcado como "negociado", com a data em que
 * combinou pagar (o desfecho "Negociado" do registro de contato grava essa data).
 */
export function promessasDaCarteira(clientes: readonly ClienteDaCarteira[], pessoaId: string, hojeIso: string): PromessasDaCarteira {
  const r: PromessasDaCarteira = { hoje: [], vencidas: [], proximas: [] };
  const limite = somarDias(hojeIso, 7);
  for (const c of clientes) {
    if (c.responsavel_cobranca_id !== pessoaId || c.status_negociacao !== 'negociado') continue;
    const dia = (c.data_pagamento_negociado ?? '').slice(0, 10);
    if (!dia || dia < hojeIso) r.vencidas.push(c);
    else if (dia === hojeIso) r.hoje.push(c);
    else if (dia <= limite) r.proximas.push(c);
  }
  const porData = (a: ClienteDaCarteira, b: ClienteDaCarteira) => (a.data_pagamento_negociado ?? '').localeCompare(b.data_pagamento_negociado ?? '');
  r.vencidas.sort((a, b) => -porData(a, b));
  r.proximas.sort(porData);
  return r;
}

// ─────────────────────────── Recuperação e comissão ───────────────────────────

/** Pago mais de 3 dias depois do vencimento: antes disso é atraso de compensação. */
export const DIAS_MINIMOS_RECUPERACAO = 3;

/** Faixas do plano de bônus (seção 5): percentual sobre o valor pago, pelo atraso no dia do pagamento. */
export const FAIXAS_RECUPERACAO = [
  { ate: 15, pct: 0, rotulo: '4 a 15 dias' },
  { ate: 30, pct: 0.015, rotulo: '16 a 30 dias' },
  { ate: 60, pct: 0.03, rotulo: '31 a 60 dias' },
  { ate: 90, pct: 0.05, rotulo: '61 a 90 dias' },
  { ate: 180, pct: 0.07, rotulo: '91 a 180 dias' },
  { ate: Infinity, pct: 0.10, rotulo: 'mais de 180 dias' },
] as const;

export const faixaDoAtraso = (dias: number): number => FAIXAS_RECUPERACAO.findIndex(f => dias <= f.ate);

export interface FaturaPaga {
  id: number;
  cpf_cnpj_cliente_boleto: string | null;
  cliente_cpf_cnpj: string | null;
  /** 'AAAA-MM-DD'. */
  vencimento_boleto: string | null;
  data_pgto_boleto: string | null;
  valor_pagamento: number | string | null;
  valor_boleto_vigor: number | string | null;
}

/** Contato registrado no VigorHub: histórico da carteira ou ação da régua concluída. */
export interface ContatoDeCobranca {
  doc: string | null;
  por: string | null;
  em: string;
}

export interface FaixaRecuperada { qtd: number; valor: number; comissao: number }

export interface Recuperacao {
  qtd: number;
  valor: number;
  comissao: number;
  porFaixa: FaixaRecuperada[];
}

const vazia = (): Recuperacao => ({ qtd: 0, valor: 0, comissao: 0, porFaixa: FAIXAS_RECUPERACAO.map(() => ({ qtd: 0, valor: 0, comissao: 0 })) });

/**
 * Recuperado por pessoa no período das faturas recebidas. Cada boleto pago com atraso vai
 * para quem registrou o ÚLTIMO contato com o cliente (CPF/CNPJ de quem paga o boleto)
 * entre o vencimento e o pagamento; sem contato nesse intervalo, fica em `semContato`.
 */
export function recuperacaoDoPeriodo(
  faturas: readonly FaturaPaga[],
  contatos: readonly ContatoDeCobranca[],
): { porPessoa: Map<string, Recuperacao>; semContato: Recuperacao } {
  const contatosPorDoc = new Map<string, ContatoDeCobranca[]>();
  for (const c of contatos) {
    const doc = soDigitos(c.doc);
    if (!doc || !c.por) continue;
    if (!contatosPorDoc.has(doc)) contatosPorDoc.set(doc, []);
    contatosPorDoc.get(doc)!.push(c);
  }

  const porPessoa = new Map<string, Recuperacao>();
  const semContato = vazia();
  for (const f of faturas) {
    if (!f.vencimento_boleto || !f.data_pgto_boleto) continue;
    const venc = f.vencimento_boleto.slice(0, 10);
    const dias = diasEntre(venc, diaLocal(f.data_pgto_boleto));
    if (dias <= DIAS_MINIMOS_RECUPERACAO) continue;
    const valor = Number(f.valor_pagamento ?? f.valor_boleto_vigor) || 0;
    const doc = soDigitos(f.cpf_cnpj_cliente_boleto) || soDigitos(f.cliente_cpf_cnpj);
    const inicio = new Date(`${venc}T00:00:00`).getTime();
    const fim = new Date(f.data_pgto_boleto).getTime();
    const ultimo = (contatosPorDoc.get(doc) ?? [])
      .filter(c => { const t = new Date(c.em).getTime(); return t >= inicio && t <= fim; })
      .sort((a, b) => b.em.localeCompare(a.em))[0];
    let alvo = semContato;
    if (ultimo) {
      if (!porPessoa.has(ultimo.por!)) porPessoa.set(ultimo.por!, vazia());
      alvo = porPessoa.get(ultimo.por!)!;
    }
    const i = faixaDoAtraso(dias);
    const comissao = valor * FAIXAS_RECUPERACAO[i].pct;
    alvo.qtd++; alvo.valor += valor; alvo.comissao += comissao;
    alvo.porFaixa[i].qtd++; alvo.porFaixa[i].valor += valor; alvo.porFaixa[i].comissao += comissao;
  }
  return { porPessoa, semContato };
}

/** Soma de várias pessoas (o time). */
export function somarRecuperacao(lista: Iterable<Recuperacao>): Recuperacao {
  const t = vazia();
  for (const r of lista) {
    t.qtd += r.qtd; t.valor += r.valor; t.comissao += r.comissao;
    r.porFaixa.forEach((f, i) => { t.porFaixa[i].qtd += f.qtd; t.porFaixa[i].valor += f.valor; t.porFaixa[i].comissao += f.comissao; });
  }
  return t;
}

// ─────────────────────────── Régua em dia (#6506) ───────────────────────────

export const META_REGUA_EM_DIA = 0.95;

export interface AcaoComExecucao extends AcaoDaRegua {
  dispensado_motivo?: string | null;
}

export interface ReguaEmDia {
  /** Ações da pessoa cujo dia caiu no período e já chegou (até ontem, ou feitas hoje). */
  devidas: number;
  emDia: number;
  /** Feitas, mas depois do dia previsto. */
  feitasComAtraso: number;
  /** Ainda pendentes com o dia já passado. */
  pendentes: number;
  taxa: number | null;
}

const resolvida = (a: AcaoComExecucao) =>
  a.status === 'concluido' || (a.status === 'dispensado' && !!(a.dispensado_motivo ?? '').trim());

/**
 * Régua em dia: ação feita, ou dispensada com motivo, até o dia previsto (vencimento + N).
 * Conta as ações da pessoa cujo dia está entre `inicioIso` e `fimIso` (inclusive) e não
 * passa de hoje; a do dia de hoje ainda pendente não conta (o dia não acabou).
 */
export function reguaEmDia(acoes: readonly AcaoComExecucao[], pessoaId: string, inicioIso: string, fimIso: string, hojeIso: string): ReguaEmDia {
  const r: ReguaEmDia = { devidas: 0, emDia: 0, feitasComAtraso: 0, pendentes: 0, taxa: null };
  const limite = fimIso < hojeIso ? fimIso : hojeIso;
  for (const a of acoes) {
    if (a.responsavel_id !== pessoaId) continue;
    const dia = diaDaAcao(a);
    if (dia < inicioIso || dia > limite) continue;
    if (resolvida(a) && a.executado_em) {
      r.devidas++;
      if (diaLocal(a.executado_em) <= dia) r.emDia++;
      else r.feitasComAtraso++;
    } else if (dia < hojeIso) {
      r.devidas++;
      r.pendentes++;
    }
  }
  r.taxa = r.devidas ? r.emDia / r.devidas : null;
  return r;
}

// ─────────────────────────── Acordos cumpridos (#6506) ───────────────────────────

export const META_ACORDOS_CUMPRIDOS = 0.85;

export interface ParcelaDoAcordo {
  id: string;
  /** 'AAAA-MM-DD'. */
  data_vencimento: string;
  status: string | null;
  /** Data do pagamento do boleto da parcela, quando pago. */
  pago_em: string | null;
  cliente: string | null;
}

export interface AcordosCumpridos {
  /** Parcelas não canceladas que venceram no período (até ontem). */
  vencidas: number;
  emDia: number;
  pagasComAtraso: number;
  emAberto: number;
  taxa: number | null;
}

/** Parcelas dos acordos da pessoa pagas até o vencimento. Parcela cancelada não conta. */
export function acordosCumpridos(parcelas: readonly ParcelaDoAcordo[], inicioIso: string, fimIso: string, hojeIso: string): AcordosCumpridos {
  const r: AcordosCumpridos = { vencidas: 0, emDia: 0, pagasComAtraso: 0, emAberto: 0, taxa: null };
  for (const p of parcelas) {
    const venc = p.data_vencimento.slice(0, 10);
    if (p.status === 'cancelado' || venc < inicioIso || venc > fimIso || venc >= hojeIso) continue;
    r.vencidas++;
    if (p.pago_em && diaLocal(p.pago_em) <= venc) r.emDia++;
    else if (p.pago_em) r.pagasComAtraso++;
    else r.emAberto++;
  }
  r.taxa = r.vencidas ? r.emDia / r.vencidas : null;
  return r;
}

// ─────────────────────────── Especialidade (#6506) ───────────────────────────

/**
 * Especialidade de cada pessoa da Cobrança (decisão de 27/10/2026): João cuida do envio
 * dos boletos ao cliente; Bruna das notificações, negativações e distratos. A Dhaiane
 * (análise cadastral) tem tela própria. Sem campo no cadastro ainda: vale o id.
 */
export type Especialidade = 'envio_boletos' | 'documentos';
export const ESPECIALIDADE_POR_PESSOA: Readonly<Record<string, Especialidade>> = {
  '2fa09e98-0d30-40d6-aa12-d3d8e4ced56c': 'envio_boletos', // João Henrique
  '4f650dd0-8caf-4eaa-b13b-467d0ac3c3d5': 'documentos', // Bruna Almeida
};

export interface BoletoAEnviar {
  id: number;
  cliente_nome: string | null;
  uc_codigo: string | null;
  mes_referencia: string | null;
  emitido_em: string;
}

/** Boletos emitidos esperando o envio, do mais antigo para o mais novo, com os dias úteis de espera. */
export function boletosAEnviar(
  boletos: readonly BoletoAEnviar[],
  hojeIso: string,
  diasUteis: (de: string, ate: string) => number,
): (BoletoAEnviar & { diasUteis: number })[] {
  return boletos
    .map(b => ({ ...b, diasUteis: Math.max(0, diasUteis(diaLocal(b.emitido_em), hojeIso)) }))
    .sort((a, b) => a.emitido_em.localeCompare(b.emitido_em));
}

/** Dos boletos enviados pela pessoa no período, quantos saíram até 1 dia útil depois da emissão. */
export function enviosEmAte1DiaUtil(
  envios: readonly { emitido_em: string | null; enviado_em: string | null }[],
  diasUteis: (de: string, ate: string) => number,
): { enviados: number; ate1Dia: number; taxa: number | null } {
  let enviados = 0, ate1Dia = 0;
  for (const e of envios) {
    if (!e.emitido_em || !e.enviado_em) continue;
    enviados++;
    if (diasUteis(diaLocal(e.emitido_em), diaLocal(e.enviado_em)) <= 1) ate1Dia++;
  }
  return { enviados, ate1Dia, taxa: enviados ? ate1Dia / enviados : null };
}
